import { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { useLocation } from "react-router-dom";
import validator from "validator";
import { APP_DOMAIN, MULTIPLE_EMAILS_ASSOCIATED_WITH_SUBSCRIPTION_ERROR } from "../../../../src/shared/constants.js";
import firebase from "../../Firebase.js";
import useGoogleSignIn from "../../hooks/useGoogleSignIn.js";
import useAppleSignIn from "../../hooks/useAppleSignIn";
import { isStagingBranch } from "../../IsStagingBranch.js";
import { useAppStore } from "../../stores/appStore.js";
import { isMetaBrowser, sendEmailSignIn } from "../../Utils";
import { StyledContainer } from "./styles";
import { styleGuideBetaSignInNext } from "../../../../src/shared/styleGuideBetaEnrollment";
import { muse2AlphaSignInNext } from "../../../../src/shared/muse2AlphaEnrollment";
import { conferenceSignInNext } from "../../../../src/shared/conferenceCampaign";

const customStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    zIndex: "20000",
    width: "fit-content",
    transform: "translate(-50%, -50%)",
    borderRadius: "15px",
    border: "0px",
    padding: "0px",
    background: "transparent",
  },
};

export default function Login({ close, history, closeable, user, loadedUser, authRedirectResultError, title }) {
  const wrapperRef = useRef(null);

  const [show, setShow] = useState(false);
  const [expectLogin, setExpectLogin] = useState(window.location.hash === "#login");
  const [showLoading, setShowLoading] = useState(false);
  const [expectLoginProblem, setExpectLoginProblem] = useState(false);

  const [email, setEmail] = useState("");
  const [devEmailLoaded, setDevEmailLoaded] = useState(false);
  const [signinEmailLinkSuccess, setSigninEmailLinkSuccess] = useState(false);
  const [signinEmailLinkError, setSigninEmailLinkError] = useState(null);
  const [stripeEmailBlocked, setStripeEmailBlocked] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showEmailDoubleLinkError, setShowEmailDoubleLinkError] = useState(false);
  const [redirectError, setRedirectError] = useState(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [activeSignInMethod, setActiveSignInMethod] = useState(null); // 'email', 'google', 'facebook', 'apple'
  const debounceTimerRef = useRef(null);
  const errorRequestRef = useRef(0);

  const anonUser = useAppStore((state) => state.anonUser);
  const mergeAccount = useAppStore((state) => state.mergeAccount);
  const setMergeAccount = useAppStore((state) => state.setMergeAccount);
  // Account exists error
  const [accountExistsError, setAccountExistsError] = useState(null);
  const [existAccounts, setExistingAccounts] = useState(null);

  const location = useLocation();
  const isConferenceEntry = !!conferenceSignInNext(location.pathname, location.search);

  // Popup failures and redirect-result failures use the same recovery UI.
  useEffect(() => {
    if (authRedirectResultError) handleSignInError(authRedirectResultError);
  }, [authRedirectResultError]);

  useEffect(() => () => {
    errorRequestRef.current += 1;
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === "development" && !devEmailLoaded) {
      fetch("/api/development/next-email")
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.email) {
            setEmail(data.email);
            setDevEmailLoaded(true);
          }
        })
        .catch(() => {});
    }
  }, []);

  // clicking outside dismisses the modal
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (closeable && close) {
          // in /app context, you can't close the modal
          close();
        }
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  // Handle various messages in the case where login should occur imminently
  useEffect(() => {
    // console.log({expectLogin, accountExistsError, existAccounts, user});
    let problemTimer;
    let messageTimer;

    function clearTimers() {
      clearTimeout(problemTimer);
      clearTimeout(messageTimer);
    }

    if (!loadedUser) {
      // we don't know log in state, so don't show login box yet
      setShow(false);
      clearTimers();
    } else if (user) {
      // user is logged in, so hide this dialogue
      errorRequestRef.current += 1;
      clearTimers();
      setShow(false);
      setExpectLogin(false);
    } else if (accountExistsError && existAccounts) {
      // if there is an error due to linking, we clear timers
      setExpectLogin(false);
      setShow(true);
      clearTimers();
    } else if (expectLogin) {
      // we expect login to happen soon (this is a redirect from auth)
      window.location.hash = "";

      setShow(false);

      messageTimer = setTimeout(() => {
        setShowLoading(true);
        setShow(true);
      }, 2000);

      // if we expect a log in and we don't after a period of time, show an error message
      problemTimer = setTimeout(() => {
        setExpectLoginProblem(true);
        setExpectLogin(false);
        setShow(true);
      }, 10000);
    } else {
      // normal case: show the login dialogue for interaction
      setShow(true);
      clearTimers();
      setShowLoading(false);
    }

    return () => {
      clearTimers();
    };
  }, [expectLogin, accountExistsError, existAccounts, user, loadedUser]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (email === "") {
      setShowEmailError(false);
      return;
    }

    if (validator.isEmail(email)) {
      setShowEmailError(false);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      setShowEmailError(true);
    }, 1500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [email]);

  const googleSignIn = useGoogleSignIn({ history });
  const appleSignIn = useAppleSignIn();

  function startSignIn(method) {
    errorRequestRef.current += 1;
    setIsSigningIn(true);
    setActiveSignInMethod(method);
    setRedirectError(null);
    setExpectLoginProblem(false);
    setAccountExistsError(null);
    setExistingAccounts(null);
  }

  function resetSignIn() {
    setIsSigningIn(false);
    setActiveSignInMethod(null);
  }

  function signInButtonText(method, label) {
    return activeSignInMethod === method ? 'Signing in...' : `Continue with ${label}`;
  }

  async function handleSignInError(error) {
    const requestId = ++errorRequestRef.current;
    resetSignIn();
    setExpectLogin(false);
    setExpectLoginProblem(false);

    if (error?.code === "auth/account-exists-with-different-credential") {
      try {
        const methods = await firebase.auth().fetchSignInMethodsForEmail(error.email);
        // A retry, newer error, successful login, or unmount supersedes this lookup.
        if (requestId !== errorRequestRef.current) return;
        setAccountExistsError(true);
        setExistingAccounts({ email: error.email, methods });
        return;
      } catch {
        // If provider lookup fails, keep the normal retry controls available.
      }
    }

    if (requestId !== errorRequestRef.current) return;
    setRedirectError("Something went wrong while signing you in. Please try again or use a different login method.");
  }

  function handleSignIn(method, signInFn) {
    if (isSigningIn) return;
    startSignIn(method);
    try {
      Promise.resolve(signInFn()).catch(handleSignInError);
    } catch (e) {
      handleSignInError(e);
    }
  }

  function facebookSignIn() {
    // Move the user to the app before signing in.
    if (history && !location.pathname.startsWith("/app/") && !location.pathname.startsWith("/pages")) {
      history.push("/?fromAuth");
    }
    const provider = new firebase.auth.FacebookAuthProvider();

    // Preview/local sites use a different auth domain. A redirect loses the
    // result when browsers partition cross-site storage, so use a popup there.
    if (process.env.NODE_ENV === "development" || process.env.REACT_APP_NODE_ENV === "development" || isStagingBranch()) {
      if (anonUser && mergeAccount) {
        return anonUser.linkWithPopup(provider)
          .catch((error) => {
            if (error.code === "auth/credential-already-in-use") {
              return firebase.auth().signInWithCredential(error.credential);
            }
            throw error;
          })
          .then(() => setMergeAccount(false));
      }
      return firebase.auth().signInWithPopup(provider);
    }

    window.location.hash = "login";
    // If current user is anonymous, link the account instead of signing in
    if (anonUser && mergeAccount) {
      // Keep the merge choice if launch fails so retries and email fallback
      // preserve the anonymous account. This state resets on redirect reload.
      return anonUser.linkWithRedirect(provider);
    } else {
      return firebase.auth().signInWithRedirect(provider);
    }
  }

  function resetErrors() {
    setStripeEmailBlocked(false);
    setSigninEmailLinkError(null);
  }

  function signinEmail(e) {
    e.preventDefault();

    if (!validator.isEmail(email)) {
      setSigninEmailLinkError("Please enter a valid email address");
      return;
    }

    if (stripeEmailBlocked) {
      return;
    }

    if (isSigningIn) return;

    if (process.env.NODE_ENV === "development") {
      fetch("/api/development/next-email", { method: "POST" }).catch(() => {});
    }

    startSignIn('email');

    const next = conferenceSignInNext(location.pathname, location.search)
      || styleGuideBetaSignInNext(location.pathname, location.search)
      || muse2AlphaSignInNext(location.pathname, location.search)
      || (location.pathname.startsWith("/pages") ? location.pathname : undefined);
    sendEmailSignIn(email, { next })
      .then(function () {
        // The link was successfully sent. Inform the user. Save the email
        // locally so you don't need to ask the user for it again if they open
        // the link on the same device.
        // TODO: Change link in the API to "/"
        setSigninEmailLinkSuccess(true);
        // if mergeAccount is false, sign the anonymous user out
        if (anonUser && !mergeAccount) {
          // this handles the case where an anonymous user did not sign up from the share link page
          // so we don't want to merge their account with the new account. So we sign them out so
          // they aren't merged with the new account when they verify their email
          firebase.auth().signOut();
        }
      })
      .catch(function (error) {
        resetSignIn();
        if (error?.status === 409 && error?.response) {
          error.response
            .json()
            .then((data) => {
              if (data?.error === MULTIPLE_EMAILS_ASSOCIATED_WITH_SUBSCRIPTION_ERROR) {
                setStripeEmailBlocked(true);
                setSigninEmailLinkError(null);
              } else {
                setSigninEmailLinkError(error.message);
              }
            })
            .catch(() => {
              setStripeEmailBlocked(true);
              setSigninEmailLinkError(null);
            });
        } else if (error?.status === 409) {
          setStripeEmailBlocked(true);
          setSigninEmailLinkError(null);
        } else {
          setSigninEmailLinkError(error.message);
        }
      });
  }

  const hasFbclid = new URLSearchParams(window.location.search).has("fbclid");
  const showGoogleLogin = !(hasFbclid && isMetaBrowser());

  // can be used to give a heads up to users about something
  const headsUp = false;

  return (
    <Modal
      isOpen={show}
      contentRef={(node) => (wrapperRef.current = node)}
      style={customStyles}
      id="login-modal"
    >
      <StyledContainer>
        {/* This is just used if we need to give the users a warning about something */}
        {headsUp && (
          <div className="heads-up">
            <p>
              👋 Welcome to the new URL! The Sudowrite app now lives at <a href={`https://${APP_DOMAIN}`}>{APP_DOMAIN}</a>.
            </p>
            <p>You can log in normally now, and all your data is safe, only the URL has changed.</p>
            <p className="small">Contact hi@sudowrite.com if you have any questions.</p>
          </div>
        )}
        {showLoading && (
          <div className="login-container">
            <h1>Logging you in</h1>
          </div>
        )}

        {signinEmailLinkSuccess && (
          <div className="login-container wide">
            <div className="heading-group">
              <h1>Check your inbox for a link to log in</h1>
              <h2>If you don't see it, check your Spam folder</h2>
            </div>
            <p>
              Still not working?{" "}
              <a
                onClick={(e) => {
                  setSigninEmailLinkSuccess(false);
                  resetErrors();
                  resetSignIn();
                }}
              >
                {" "}
                Try again
              </a>
            </p>
          </div>
        )}

        {!signinEmailLinkSuccess && !expectLogin && (
          <div className="login-container">
            {expectLoginProblem && (
              <div className="error">Oops, looks like something went wrong with logging in. We recommend disabling any extensions, VPN, or antivirus software and trying again.</div>
            )}

            {accountExistsError && existAccounts && (
              <div className="error">
                You already have an account with email {existAccounts.email} using
                {existAccounts.methods
                  .map((method) => {
                    if (method === "google.com") {
                      return " Google login";
                    } else if (method === "apple.com") {
                      return " Apple login";
                    } else if (method === "emailLink") {
                      return " Email";
                    } else {
                      return method;
                    }
                  })
                  .join(" and ")}
                . Use {existAccounts.methods.length > 1 ? "those methods" : "that method"} instead.
              </div>
            )}

            {redirectError && (
              <div className="error">{redirectError}</div>
            )}

            {isConferenceEntry && <><h1>Join us at Author Nation</h1><p>Sign up or log in to choose your plan.</p></>}
            {!isConferenceEntry && title && <h1>{title}</h1>}
            {!isConferenceEntry && !title && (
              <>
                <h1>Love writing again</h1>
                <p>
                  Try Sudowrite completely risk-free.
                </p>
              </>
            )}

            <small>
              By signing in, you agree to Sudowrite's{" "}
              <a
                href="https://sudowrite.notion.site/Terms-and-Conditions-83ddd78e1ab04d9d88321a9ef7665925"
                target="_blank"
              >
                Terms and Conditions
              </a>{" "}
              and{" "}
              <a
                href="https://sudowrite.notion.site/Privacy-Policy-710e53e814f8491b8ee2634ee9162ed8"
                target="_blank"
              >
                Privacy Policy
              </a>
              . They're both pretty reasonable, though.
            </small>

            {showGoogleLogin && (
              <button
                className="auth"
                disabled={isSigningIn}
                onClick={() => handleSignIn('google', googleSignIn)}
              >
                <img src="/assets/google.png" /> {signInButtonText('google', 'Google')}
              </button>
            )}

            <button
              className="auth"
              disabled={isSigningIn}
              onClick={() => handleSignIn('facebook', facebookSignIn)}
            >
              <img src="/assets/facebook.png" />
              {signInButtonText('facebook', 'Facebook')}
            </button>

            <button
              className="auth no-margin"
              disabled={isSigningIn}
              onClick={() => handleSignIn('apple', appleSignIn)}
            >
              <img src="/assets/apple.svg" />
              {signInButtonText('apple', 'Apple')}
            </button>

            <div className="or">OR</div>

            <form onSubmit={signinEmail}>
              <div className={`email-input-container ${showEmailError || stripeEmailBlocked ? "has-error" : ""}`}>
                <input
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (stripeEmailBlocked) {
                      setStripeEmailBlocked(false);
                      setSigninEmailLinkError(null);
                    }
                  }}
                  type="text"
                  className="email"
                  placeholder="Email"
                  disabled={isSigningIn}
                />
                {showEmailError && <p className="email-error">Invalid Email</p>}
                {stripeEmailBlocked && (
                  <p className="email-error">
                    You have multiple emails associated with your Sudowrite account, please {""}
                    <a
                      href={`mailto:hi@sudowrite.com?subject=Mulitiple emails associated my Sudowrite account&body=I tried to sign in with ${email} and was informed it is associated with an existing Stripe subscription. Please help me access my account.%0D%0A%0D%0A**Please include any other emails you think might be associated with your account, previous emails you have used to sign in with, or other information you think is relevant.**`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      contact customer support
                    </a>
                    .
                  </p>
                )}
              </div>
              <button
                disabled={!validator.isEmail(email) || stripeEmailBlocked || isSigningIn}
                className="email-button"
              >
                {signInButtonText('email', 'Email')}
              </button>
            </form>

            {signinEmailLinkError && !stripeEmailBlocked ? <div className="error">Error: {signinEmailLinkError}</div> : null}
          </div>
        )}
      </StyledContainer>
    </Modal>
  );
}
